// src/components/negocios/NegocioDetailsModal.jsx
import React, { useEffect, useMemo } from 'react';
import {
  X,
  MapPin,
  User2,
  BadgeCheck,
  Shuffle,
  Phone,
  DollarSign,
  Info,
  ClipboardList,
  Shield,
} from 'lucide-react';
import { formatBRL } from '../dashboard/mockData';
import { useAuth } from '../../context/AuthContext';

/* =========================================================
  Helpers
========================================================= */
function abreviarBRL(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const abs = Math.abs(n);

  if (abs >= 1_000_000_000) {
    const v = (n / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1);
    return `R$ ${String(v).replace('.', ',')} Bi`;
  }
  if (abs >= 1_000_000) {
    const v = (n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1);
    return `R$ ${String(v).replace('.', ',')} Mi`;
  }
  if (abs >= 1_000) {
    const v = (n / 1_000).toFixed(abs >= 100_000 ? 0 : 1);
    return `R$ ${String(v).replace('.', ',')} mil`;
  }
  return formatBRL(n);
}

function buildWhatsAppLink(rawNumber, message = '') {
  const digits = String(rawNumber || '').replace(/[^\d]/g, '');
  if (!digits) return null;

  const phone = digits.startsWith('55') ? digits : `55${digits}`;
  const base = `https://wa.me/${phone}`;
  const text = String(message || '').trim();
  if (!text) return base;
  return `${base}?text=${encodeURIComponent(text)}`;
}

function calcFlexibilidade(negocio) {
  const bairros = negocio?.bairros || negocio?.bairrosDesejados || [];
  const count = Array.isArray(bairros) ? bairros.length : 0;

  const explicit =
    negocio?.flexibilidadeNivel ||
    negocio?.flexibilidade ||
    negocio?.nivelFlexibilidade ||
    '';

  const exp = String(explicit || '').toLowerCase();
  if (exp.includes('alta'))
    return { level: 'alta', label: 'Alta flexibilidade' };
  if (exp.includes('media') || exp.includes('média'))
    return { level: 'media', label: 'Flexibilidade média' };
  if (exp.includes('baixa'))
    return { level: 'baixa', label: 'Baixa flexibilidade' };

  if (count >= 4) return { level: 'alta', label: 'Alta flexibilidade' };
  if (count >= 2) return { level: 'media', label: 'Flexibilidade média' };
  return { level: 'baixa', label: 'Baixa flexibilidade' };
}

function flexDotClass(level) {
  if (level === 'alta') return 'bg-emerald-500';
  if (level === 'media') return 'bg-amber-500';
  return 'bg-zinc-400';
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function KeyValue({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">
        {label}
      </div>
      <div className="mt-1 text-sm text-zinc-900 break-words">{children}</div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 text-zinc-500" /> : null}
        <div className="text-sm font-semibold text-zinc-900">{title}</div>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Chip({ children, className = '' }) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold',
        className,
      ].join(' ')}
    >
      {children}
    </span>
  );
}

/* =========================================================
  Modal central (compacto + privacidade)
========================================================= */
export default function NegocioDetailsModal({
  open,
  onClose,
  negocio,
  modo = 'meu', // 'meu' | 'comunidade'
  onEditar, // opcional
}) {
  const { usuario } = useAuth() || {};

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, [open]);

  const bairros = useMemo(() => {
    const arr = negocio?.bairros || negocio?.bairrosDesejados || [];
    return safeArray(arr)
      .map(String)
      .map((s) => s.trim())
      .filter(Boolean);
  }, [negocio]);

  const flex = useMemo(() => calcFlexibilidade(negocio), [negocio]);

  const tipoImovel =
    String(negocio?.tipoImovel || negocio?.tipo || '—').trim() || '—';

  const objetivo =
    String(
      negocio?.objetivoLabel ||
        negocio?.objetivo ||
        (negocio?.buscaTags?.length ? negocio.buscaTags[0] : '') ||
        '—'
    ).trim() || '—';

  const titulo =
    String(
      negocio?.tituloNegocio ||
        negocio?.tituloAnuncio ||
        negocio?.tituloNegocioLegacy ||
        ''
    ).trim() || '';

  const cidade =
    String(negocio?.cidade || negocio?.cidadePrincipal || '').trim() ||
    'Cidade não informada';

  const regiao =
    String(negocio?.regiao || negocio?.zona || negocio?.setor || '').trim() ||
    '';

  // 🔒 PRIVACIDADE
  const corretorId =
    String(
      negocio?.corretorId || negocio?.uidCorretor || negocio?.ownerUid || ''
    ).trim() || '';
  const isAutor = !!usuario?.uid && !!corretorId && usuario.uid === corretorId;

  // Cliente só aparece quando autor (senão, fica oculto)
  const nomeCliente = isAutor
    ? String(
        negocio?.nomeCliente || negocio?.clientePrimeiroNome || ''
      ).trim() || '—'
    : null;

  const corretorNome =
    String(
      negocio?.nomeCorretor ||
        negocio?.corretorNome ||
        negocio?.corretorNomePublico ||
        ''
    ).trim() || 'Corretor';

  const corretorEspecialidade =
    String(
      negocio?.corretorEspecialidade || negocio?.especialidade || ''
    ).trim() || '—';

  const valorMin = Number(negocio?.valorMin ?? null);
  const valorMax = Number(negocio?.valorMax ?? null);
  const valorPretendido = Number(negocio?.valorPretendido ?? null);

  const ticket = useMemo(() => {
    if (
      Number.isFinite(valorMin) &&
      Number.isFinite(valorMax) &&
      valorMin > 0 &&
      valorMax > 0
    ) {
      return `${abreviarBRL(valorMin)} – ${abreviarBRL(valorMax)}`;
    }
    if (Number.isFinite(valorPretendido) && valorPretendido > 0)
      return abreviarBRL(valorPretendido);
    const fallback = Number(negocio?.valor ?? null);
    return Number.isFinite(fallback) && fallback > 0
      ? abreviarBRL(fallback)
      : '—';
  }, [valorMin, valorMax, valorPretendido, negocio]);

  const observacoes =
    String(
      negocio?.observacoes_corretor ||
        negocio?.observacoes ||
        negocio?.descricao ||
        ''
    ).trim() || '—';

  // ✅ Contato SEMPRE do corretor dono do negócio (nunca do cliente)
  const whatsappCorretorRaw = String(
    negocio?.whatsappCorretor ||
      negocio?.corretorWhatsapp ||
      negocio?.whatsapp ||
      ''
  ).trim();

  const whatsappCorretorLink = useMemo(() => {
    if (!whatsappCorretorRaw) return null;
    const msg = `Olá ${corretorNome}! Vi seu negócio na Central de Negócios da Elion e queria mais informações.`;
    return buildWhatsAppLink(whatsappCorretorRaw, msg);
  }, [whatsappCorretorRaw, corretorNome]);

  const permutaAceita = !!negocio?.aceitaPermuta || !!negocio?.permuta?.aceita;
  const permutaItens = safeArray(negocio?.permuta?.itens);
  const permutaFaixa =
    String(
      negocio?.permuta?.faixa || negocio?.percentualPermutaFaixa || ''
    ).trim() || '—';

  const entradaDisponivel = Number(negocio?.entrada_disponivel ?? null);
  const itensDisponiveis = safeArray(negocio?.itens_disponiveis_ids);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 p-4 md:p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="min-h-full w-full flex items-center justify-center">
        {/* ✅ Menor + mais bonito: max-w-3xl + max-h + scroll interno */}
        <div className="w-full max-w-3xl max-h-[85vh] overflow-hidden">
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-2xl shadow-zinc-900/10 overflow-hidden">
            {/* Header compacto */}
            <div className="flex items-start justify-between gap-4 border-b border-zinc-100 px-5 py-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip className="border-zinc-200 bg-zinc-50 text-zinc-700">
                    {tipoImovel}
                  </Chip>
                  <Chip className="border-zinc-200 bg-white text-zinc-700">
                    {objetivo}
                  </Chip>
                  <Chip className="border-zinc-200 bg-white text-zinc-700 gap-2">
                    <span
                      className={[
                        'h-2 w-2 rounded-full',
                        flexDotClass(flex.level),
                      ].join(' ')}
                    />
                    {flex.label}
                  </Chip>

                  {permutaAceita && (
                    <Chip className="border-blue-200 bg-blue-50 text-blue-700 gap-1">
                      <Shuffle className="h-3 w-3" />
                      Permuta
                    </Chip>
                  )}
                </div>

                <div className="mt-2 text-lg font-extrabold text-zinc-900 tracking-tight truncate">
                  {titulo || 'Detalhes do negócio'}
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-zinc-600">
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-zinc-400" />
                    {cidade}
                  </span>
                  {regiao ? <span className="text-zinc-300">•</span> : null}
                  {regiao ? <span>Região: {regiao}</span> : null}
                </div>
              </div>

              <div className="shrink-0 flex items-center gap-2">
                {modo === 'meu' && typeof onEditar === 'function' && (
                  <button
                    type="button"
                    onClick={() => onEditar?.(negocio)}
                    className="rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                  >
                    Editar
                  </button>
                )}

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center rounded-full border border-zinc-200 bg-white h-9 w-9 text-zinc-600 hover:bg-zinc-50"
                  title="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Body com scroll */}
            <div className="max-h-[calc(85vh-72px)] overflow-auto px-5 py-4 space-y-3">
              {/* Linha 1: Contatos + Ticket/Local (mais compacto) */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {/* Contatos (privacidade aplicada) */}
                <SectionCard title="Contatos" icon={User2}>
                  <div className="space-y-3">
                    {/* Corretor sempre */}
                    <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                        Corretor responsável
                      </div>

                      <div className="mt-1 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-zinc-900 truncate">
                            {corretorNome}
                          </div>
                          <div className="text-[11px] text-zinc-500 truncate">
                            {corretorEspecialidade}
                          </div>
                        </div>

                        {whatsappCorretorLink ? (
                          <a
                            href={whatsappCorretorLink}
                            target="_blank"
                            rel="noreferrer"
                            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <Phone className="h-4 w-4" />
                            WhatsApp
                          </a>
                        ) : (
                          <span className="shrink-0 text-xs text-zinc-400">
                            Contato não informado
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Cliente só se autor */}
                    {isAutor ? (
                      <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                          Cliente (somente você)
                        </div>
                        <div className="mt-1 text-sm font-extrabold text-zinc-900">
                          {nomeCliente}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-zinc-200 bg-white p-3">
                        <div className="flex items-start gap-2">
                          <Shield className="h-4 w-4 text-zinc-400 mt-0.5" />
                          <div className="text-xs text-zinc-600">
                            <span className="font-semibold text-zinc-800">
                              Dados do cliente ocultos.
                            </span>{' '}
                            Para avançar, fale com o corretor responsável.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </SectionCard>

                {/* Ticket + Local dentro da mesma coluna */}
                <div className="grid gap-3">
                  <SectionCard title="Ticket" icon={DollarSign}>
                    <div className="text-xl font-extrabold text-zinc-900">
                      {ticket}
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">
                      Base em valores informados
                    </div>

                    {Number.isFinite(entradaDisponivel) &&
                    entradaDisponivel > 0 ? (
                      <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                          Entrada disponível
                        </div>
                        <div className="mt-1 text-sm font-bold text-emerald-900">
                          {abreviarBRL(entradaDisponivel)}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 text-sm text-zinc-500">
                        Entrada disponível:{' '}
                        <span className="font-semibold">não informada</span>
                      </div>
                    )}
                  </SectionCard>

                  <SectionCard title="Local & foco" icon={MapPin}>
                    <div className="grid gap-2">
                      <KeyValue label="Cidade">{cidade}</KeyValue>
                      <KeyValue label="Região">{regiao || '—'}</KeyValue>

                      <KeyValue label="Bairros">
                        {bairros.length ? (
                          <div className="flex flex-wrap gap-2">
                            {bairros.slice(0, 4).map((b) => (
                              <span
                                key={b}
                                className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-xs font-semibold text-zinc-700"
                              >
                                {b}
                              </span>
                            ))}
                            {bairros.length > 4 ? (
                              <span className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-xs font-semibold text-zinc-500">
                                +{bairros.length - 4}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          'Não informados'
                        )}
                      </KeyValue>
                    </div>
                  </SectionCard>
                </div>
              </div>

              {/* Linha 2: Permuta + Observações */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <SectionCard title="Permuta" icon={Shuffle}>
                  {permutaAceita ? (
                    <div className="space-y-3">
                      <KeyValue label="Percentual">{permutaFaixa}</KeyValue>

                      <KeyValue label="Tipos aceitos">
                        {permutaItens.length ? (
                          <div className="space-y-2">
                            {permutaItens.map((it, idx) => {
                              const label =
                                it?.tipo || it?.key || `Item ${idx + 1}`;
                              const v = Number(it?.valorAproximado ?? 0);
                              return (
                                <div
                                  key={`${label}-${idx}`}
                                  className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white px-3 py-2"
                                >
                                  <div className="text-sm font-semibold text-zinc-800">
                                    {label}
                                  </div>
                                  <div className="text-sm font-bold text-zinc-900">
                                    {v > 0 ? abreviarBRL(v) : '—'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-sm text-zinc-600">
                            Aceita permuta, mas os itens não foram detalhados.
                          </div>
                        )}
                      </KeyValue>

                      <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                        <div className="flex items-center gap-2 font-semibold">
                          <BadgeCheck className="h-4 w-4" />
                          Dica do sistema
                        </div>
                        <div className="mt-1 text-blue-800">
                          Quanto mais detalhada a permuta (tipo + valor), mais
                          “VERDE” ficam os matches.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-600">
                      Não informado como negócio com permuta.
                    </div>
                  )}
                </SectionCard>

                <SectionCard title="Observações do negócio" icon={Info}>
                  <div className="text-sm text-zinc-800 whitespace-pre-wrap">
                    {observacoes}
                  </div>

                  {/* ✅ contato do corretor (sempre) */}
                  <div className="mt-4 rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                      Contato do corretor
                    </div>

                    {whatsappCorretorLink ? (
                      <a
                        href={whatsappCorretorLink}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Phone className="h-4 w-4" />
                        Chamar no WhatsApp
                      </a>
                    ) : (
                      <div className="mt-2 text-sm text-zinc-600">
                        WhatsApp não informado.
                      </div>
                    )}
                  </div>
                </SectionCard>
              </div>

              {/* Linha 3: Técnicos colapsável (pra ficar menor) */}
              <details className="rounded-2xl border border-zinc-200 bg-white p-4">
                <summary className="cursor-pointer flex items-center gap-2 text-sm font-semibold text-zinc-900">
                  <ClipboardList className="h-4 w-4 text-zinc-500" />
                  Dados para Match (técnico)
                </summary>

                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <KeyValue label="itens_disponiveis_ids">
                    {itensDisponiveis.length ? (
                      <div className="text-sm text-zinc-800">
                        {itensDisponiveis.join(', ')}
                      </div>
                    ) : (
                      '—'
                    )}
                  </KeyValue>

                  <KeyValue label="entrada_disponivel">
                    {Number.isFinite(entradaDisponivel) && entradaDisponivel > 0
                      ? abreviarBRL(entradaDisponivel)
                      : '—'}
                  </KeyValue>

                  <KeyValue label="ID do negócio">
                    <span className="font-mono text-xs text-zinc-700">
                      {negocio?.id || negocio?.negocioId || '—'}
                    </span>
                  </KeyValue>
                </div>
              </details>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
                >
                  Fechar
                </button>
              </div>
            </div>

            {/* ✅ Acessibilidade: ESC fecha */}
            <EscToClose onClose={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
  ESC fecha
========================================================= */
function EscToClose({ onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return null;
}
